import { useRef, useState } from "react";
import uploadIcon from "../upload-icon.png";
import { uploadVideos } from "../api";
import {
  describeUploadError,
  summarizeUploadResponse,
  UPLOAD_STATUS_LABEL,
} from "../utils/parsers";

export default function UploadTab({ showToast }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0 || uploading) return;

    setUploading(true);
    setOutcome(null);
    showToast(
      "uploading",
      `Uploading ${files.length} video${files.length === 1 ? "" : "s"}...`,
    );
    try {
      const response = await uploadVideos(files);
      const summary = summarizeUploadResponse(
        response.data,
        response.status,
        files.map((f) => f.name),
      );
      setOutcome(summary);
      showToast(summary.tone, summary.message);
    } catch (error) {
      const message = describeUploadError(error);
      setOutcome({
        results: files.map((f) => ({ fileName: f.name, status: "failed", error: message })),
        counts: { stored: 0, duplicate: 0, invalid: 0, failed: files.length },
        tone: "error",
        message,
      });
      showToast("error", message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div
        className={`upload-outline ${uploading ? "uploading" : ""}`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
      >
        <div className="upload-content">
          <img
            src={uploadIcon}
            className="App-upload"
            alt="Upload videos"
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ cursor: uploading ? "wait" : "pointer" }}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            multiple
            disabled={uploading}
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = ""; // allow picking the same file again
            }}
            style={{ display: "none" }}
          />
          <p className="text">
            {uploading ? "Uploading, please wait..." : "Drag & Drop video here or click to upload"}
          </p>
        </div>
      </div>

      {outcome && (
        <div className={`upload-results ${outcome.tone}`} data-testid="upload-results">
          <div className="upload-results-title">{outcome.message}</div>
          <ul>
            {outcome.results.map((r, i) => (
              <li key={`${r.fileName}-${i}`} className={`upload-result ${r.status}`}>
                <span className="upload-result-status">{r.label ?? UPLOAD_STATUS_LABEL[r.status]}</span>
                <span className="upload-result-name">
                  {r.fileName}
                  {r.storedAs && r.storedAs !== r.fileName ? ` (stored as ${r.storedAs})` : ""}
                </span>
                {r.detail && <span className="upload-result-detail">{r.detail}</span>}
                {r.error && <span className="upload-result-error">{r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
